/**
 * Plain inline-edit cells for atlas Table grids.
 *
 * Behaviour: every cell renders as a normal text/date input. Click anywhere
 * to focus, type to edit, click another cell (or Tab) to move on. No
 * read-mode/edit-mode toggle, no keyboard interception, no Excel-style
 * arrow navigation.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * TODO(future): Excel-like keyboard navigation
 *
 * The previous version of this file implemented an Excel-style flow:
 *   • Cells render as plain text by default
 *   • Click → enters edit mode
 *   • Arrow keys move between cells without entering edit mode
 *   • Enter enters edit mode; Tab moves to next cell; Esc reverts
 *
 * That worked but fought react-aria-components' Table grid keyboard
 * handling. To make typing inside an edit-mode input behave normally we
 * had to intercept arrow / Home / End on a window-level capture listener,
 * which had a side-effect: under certain timing, single Space presses
 * would "stick" — a typed space would only register after several presses.
 *
 * The user opted to drop Excel-style nav for now in favour of plain
 * inputs. To bring it back later, the path is:
 *   1. Add `keyboardNavigationBehavior="tab"` on AriaTable in
 *      packages/atlas-ui/src/components/application/table/table.tsx so
 *      arrow keys do NOT trigger react-aria's grid nav.
 *   2. Wrap each cell in a focusable read-mode container (e.g. a button
 *      that switches state on Enter/click).
 *   3. Implement a small focus-manager (cell coords → DOM lookup) that
 *      arrow keys can use only when the user is OUTSIDE an input.
 * The version we deleted is in git history at the previous commit on
 * feature/orion-fe-import (see EditableCellProvider's old useEffect).
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { AlertCircle } from "@untitledui/icons";
import { Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";

/** Wraps a region of editable cells. Installs a window-level capture
 *  listener that quarantines ALL keydown events whose target is an
 *  editable input inside this region — so react-aria's Table grid
 *  keyboard handling (arrow nav, Space-as-toggle, type-ahead) stops
 *  interfering with the input's native behaviour. The browser still
 *  performs every default action (typing characters including space,
 *  caret movement with arrows, Tab focus traversal, Backspace/Delete,
 *  Cmd/Option+Arrow word/line jumps). */
export function EditableCellProvider({ children }: { children: ReactNode }) {
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const intercept = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            // Only act when focus is in an editable text/number/date input
            // inside this provider. Read-only/disabled inputs and elements
            // outside the region keep react-aria's behaviour.
            const isEditable =
                (target.tagName === "INPUT" || target.tagName === "TEXTAREA") &&
                !(target as HTMLInputElement).readOnly &&
                !(target as HTMLInputElement).disabled &&
                root.contains(target);
            if (!isEditable) return;
            // Kill propagation so no other handler (react-aria's grid nav,
            // type-ahead, selection) sees the key. Do NOT preventDefault —
            // the browser still applies the native action on the input.
            e.stopImmediatePropagation();
        };

        // Window-level capture so we fire before React's delegated
        // listener (which lives at the React root, lower in the chain).
        window.addEventListener("keydown", intercept, true);
        return () => window.removeEventListener("keydown", intercept, true);
    }, []);

    // `display: contents` keeps this wrapper out of the layout (it only exists
    // for the keyboard-interception ref) so it doesn't break the flex chain
    // when a table fills available height.
    return (
        <div ref={rootRef} className="contents">
            {children}
        </div>
    );
}

// ─────────────────────────── Shared cell shell ─────────────────

/** Wrapper sizing + border baseline. Identical for valid/invalid so toggling
 *  never reflows the surrounding row. */
const SHARED_CELL = "relative flex h-9 w-full items-center rounded-lg border text-sm transition-colors focus-within:border-brand-primary";

/** Inline padding: 12px on both sides for the content; pr-9 when invalid
 *  to leave room for the absolute icon at the right. */
const CONTENT_PAD = "px-3";

const INPUT_CLASSNAME = "h-full w-full bg-transparent text-primary outline-none placeholder:text-placeholder";

function cellBorderColor(isInvalid?: boolean): string {
    return isInvalid ? "var(--color-border-error)" : "transparent";
}

/**
 * The red AlertCircle that sits at the right edge of an invalid cell.
 * Wrapped in its own atlas Tooltip+TooltipTrigger so the popover is
 * anchored to the icon (not the whole field) and the user only needs
 * to hover the icon to read the error.
 *
 * Auto-show: when this component mounts (i.e., the field has just
 * become invalid — typically on submit), the tooltip pops up on its
 * own for ~3 s so the user immediately sees the message without
 * having to hover. After that, hover/focus drive it normally via
 * react-aria's controlled isOpen flow.
 */
function InvalidIconTooltip({ message, className }: { message?: string; className?: string }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // Mount = invalid just became true → flash the tooltip.
        setOpen(true);
        const t = window.setTimeout(() => setOpen(false), 3000);
        return () => window.clearTimeout(t);
    }, []);

    const icon = <AlertCircle aria-hidden="true" className="text-fg-error-secondary size-4 stroke-[2.25px]" />;

    if (!message) {
        return <span className={cx("pointer-events-none absolute", className)}>{icon}</span>;
    }
    return (
        <Tooltip title={message} placement="top" arrow isOpen={open} onOpenChange={setOpen}>
            <TooltipTrigger className={cx("absolute cursor-help", className)} excludeFromTabOrder>
                {icon}
            </TooltipTrigger>
        </Tooltip>
    );
}

// ─────────────────────────── Text cell ────────────────────────

interface EditableTextCellProps {
    "data-autotest"?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    ariaLabel: string;
    isInvalid?: boolean;
    errorMessage?: string;
    /** Hint to mobile keyboards / screen readers that the input expects a
     *  specific kind of content (e.g. "numeric" for ZIP codes). */
    inputMode?: "text" | "numeric" | "decimal" | "tel" | "email" | "url";
    /** Hard cap on character count, applied via the underlying <input>. */
    maxLength?: number;
    /** Optional last-mile transformation applied to every keystroke value
     *  before it reaches `onChange` — useful to strip non-digits, etc. */
    sanitize?: (raw: string) => string;
    /** Optional display formatter — converts the canonical stored value to
     *  the string shown in the input (e.g. "1234.5" → "1,234.5"). The
     *  underlying form state always holds the sanitized raw value; only
     *  the visible text is formatted. */
    format?: (raw: string) => string;
    /** Static suffix glyph rendered as a sibling to the right of the
     *  input (NOT part of the input value) — e.g. "%" for percentage
     *  cells. Hidden while the value is empty so the placeholder can
     *  carry the full "0%" preview itself. */
    suffix?: string;
    /** Text alignment inside the input. Numeric columns whose read-only
     *  siblings are right-aligned (e.g. the Acuity census grid) pass
     *  "right" so edited and derived values line up. */
    align?: "left" | "right";
    /** Renders the cell as non-editable while keeping the value visible.
     *  Used by archived facility rows (OR-1431), which stay on the
     *  submission read-only until restored. Drops the hover affordance and
     *  the placeholder, and marks the input `readOnly` — which the
     *  EditableCellProvider already knows to ignore for click-to-edit. */
    isReadOnly?: boolean;
}

export function EditableTextCell({
    "data-autotest": dataAutotest,
    value,
    onChange,
    placeholder = "—",
    ariaLabel,
    isInvalid,
    errorMessage,
    inputMode,
    maxLength,
    sanitize,
    format,
    suffix,
    align = "left",
    isReadOnly,
}: EditableTextCellProps) {
    // Local buffer so each keystroke paints instantly, decoupled from the
    // (possibly heavy, now-deferred) parent re-render / derived recalc the
    // change triggers downstream.
    const inputRef = useRef<HTMLInputElement>(null);
    const lastEmitted = useRef(value);
    const [local, setLocal] = useState(value);
    // Re-sync ONLY on genuine external changes (autofill, save, a sibling
    // section editing the same row) — never on the lagging echo of our own
    // emit, so a fast typist never sees a character revert.
    //
    // While the input is focused the local buffer is authoritative: the SoT
    // write is deferred (startTransition, see useDeferredSetter) so `value`
    // echoes back late, and under fast typing an INTERMEDIATE echo can arrive
    // out-of-phase with `lastEmitted` and clobber a half-typed value (OR-1316).
    // The `lastEmitted` string guard is defeatable in that race, so we also
    // skip resync entirely whenever the user is actively editing this cell.
    useEffect(() => {
        if (document.activeElement === inputRef.current) return;
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setLocal(value);
        }
    }, [value]);

    const displayValue = format ? format(local) : local;
    const commit = (raw: string) => {
        const next = sanitize ? sanitize(raw) : raw;
        lastEmitted.current = next;
        setLocal(next);
        onChange(next);
    };
    return (
        <div
            className={cx(SHARED_CELL, CONTENT_PAD, !isReadOnly && "hover:bg-secondary_hover", isInvalid && "pr-9")}
            style={{ borderColor: cellBorderColor(isInvalid) }}
        >
            <input
                ref={inputRef}
                type="text"
                inputMode={inputMode}
                maxLength={maxLength}
                aria-label={ariaLabel}
                data-autotest={dataAutotest}
                aria-invalid={isInvalid || undefined}
                readOnly={isReadOnly}
                tabIndex={isReadOnly ? -1 : undefined}
                value={displayValue}
                onChange={(e) => commit(e.target.value)}
                // An archived row shows its stored value, not a prompt to fill
                // one in — an empty cell should read as empty, not "—".
                placeholder={isReadOnly ? undefined : placeholder}
                className={cx(
                    INPUT_CLASSNAME,
                    suffix && "min-w-0 flex-1",
                    align === "right" && "text-right tabular-nums",
                    isReadOnly && "cursor-default",
                )}
            />
            {suffix && local && (
                <span aria-hidden="true" className="text-secondary pointer-events-none ml-0.5 text-sm select-none">
                    {suffix}
                </span>
            )}
            {isInvalid && (
                <InvalidIconTooltip
                    message={errorMessage}
                    className="right-3 top-1/2 -translate-y-1/2"
                />
            )}
        </div>
    );
}

// ─────────────────────── Invalid shell wrapper ────────────────

/**
 * Wraps a non-text cell control (Select, SearchSelect, custom money
 * dropdown, …) with the same invalid affordance EditableTextCell
 * provides: a red AlertCircle pinned to the right of the control plus
 * a hover tooltip carrying the error message. Use on dropdown cells
 * inside editable tables so the user gets the same feedback as on
 * text inputs without having to redesign each control.
 *
 *   <InvalidShell isInvalid={…} errorMessage={…}>
 *     <Select … />
 *   </InvalidShell>
 *
 * The icon sits inside the wrapper at `right-8` so it clears the
 * native chevron of atlas Select / SearchSelect (which lives at
 * right-3). When clean, the wrapper is a pass-through.
 */
interface InvalidShellProps {
    isInvalid?: boolean;
    errorMessage?: string;
    /** Extra horizontal slack when invalid (icon room). Most cells need
     *  this; pass `false` if the parent handles spacing itself. */
    reserveSpace?: boolean;
    children: ReactNode;
}

export function InvalidShell({ isInvalid, errorMessage, reserveSpace = true, children }: InvalidShellProps) {
    if (!isInvalid) return <>{children}</>;

    return (
        <div className={cx("relative w-full", reserveSpace && "pr-7")}>
            {children}
            <InvalidIconTooltip
                message={errorMessage}
                className="right-8 top-1/2 -translate-y-1/2"
            />
        </div>
    );
}

// ─────────────────────────── Date cell ────────────────────────

/** Apply MM/DD/YYYY masking as the user types. Pure — drives both the
 *  controlled draft state and the value forwarded to react-hook-form. */
function formatDateDigits(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length === 0) return "";
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    return [mm, dd, yyyy].filter(Boolean).join("/");
}

interface EditableDateCellProps {
    "data-autotest"?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    ariaLabel: string;
    isInvalid?: boolean;
    errorMessage?: string;
    /** Non-editable but still readable — see `EditableTextCellProps`. */
    isReadOnly?: boolean;
}

export function EditableDateCell({
    "data-autotest": dataAutotest,
    value,
    onChange,
    placeholder = "MM/DD/YYYY",
    ariaLabel,
    isInvalid,
    errorMessage,
    isReadOnly,
}: EditableDateCellProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [draft, setDraft] = useState(() => formatDateDigits(value));

    // Keep the local draft in sync when the upstream value changes — form
    // resets, optimistic rollbacks, etc. — but NEVER while the user is typing
    // here: the SoT write is deferred (startTransition), so its late echo of
    // `value` would overwrite the just-typed digits mid-keystroke and leave
    // only the last character (OR-1316). Focused input → local draft wins.
    useEffect(() => {
        if (document.activeElement === inputRef.current) return;
        setDraft(formatDateDigits(value));
    }, [value]);

    return (
        <div
            className={cx(SHARED_CELL, CONTENT_PAD, !isReadOnly && "hover:bg-secondary_hover", isInvalid && "pr-9")}
            style={{ borderColor: cellBorderColor(isInvalid) }}
        >
            <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                maxLength={10}
                autoComplete="off"
                aria-label={ariaLabel}
                data-autotest={dataAutotest}
                aria-invalid={isInvalid || undefined}
                readOnly={isReadOnly}
                tabIndex={isReadOnly ? -1 : undefined}
                value={draft}
                onChange={(e) => {
                    const formatted = formatDateDigits(e.target.value);
                    setDraft(formatted);
                    onChange(formatted);
                }}
                placeholder={isReadOnly ? undefined : placeholder}
                className={cx(INPUT_CLASSNAME, isReadOnly && "cursor-default")}
            />
            {isInvalid && (
                <InvalidIconTooltip
                    message={errorMessage}
                    className="right-3 top-1/2 -translate-y-1/2"
                />
            )}
        </div>
    );
}
